// src/data/proposalCost.js
//
// Item 27 / master list 5.7 / ISS-30 (2026-10-16): THE SECOND CALCULATOR - WHAT A PROPOSAL REALLY COSTS. Bob: "a second
// calculator to work off the same matrix but with the added chore of cross referencing the PROPOSED changes and its real
// cost (less whatever costs to the membership that is included in the agreement (i.e., health and welfare, insurance,
// etc.)". A BARGAINING tool, not a claims tool. ISS-30's five design consequences, each held here:
//   1. gross is not net - the result is gross, the member-borne deductions itemised, and the net; never one number;
//   2. every deduction traces to a provision - a deduction without one is refused; no assumed percentages, no averages;
//   3. it shows its work - every line says how its figure was reached;
//   4. per agreement, never across - one bargaining unit at a time; there is no comparison of two carriers;
//   5. the output is a projection [U] - it informs a chairman and never enters a claim (nothing here is stored).
// The matrix: a percentage or cents-an-hour line is priced on the unit's LAST SOURCED base rate (claimValue.js
// lastSourcedRate - the same table the claim calculator reads). Where the unit has no sourced rate the line is refused
// and says so; a placeholder rate never prices a proposal.
//
// PURE. The base rate is passed in (the panel reads it), so this file has no imports and no clock.

export const LINE_KINDS = Object.freeze({
  gwi_percent: 'General wage increase (% of the base rate)',
  hourly_increase: 'Increase in cents an hour',
  lump_sum: 'Lump-sum payment (per member)',
  other_annual: 'Other annual value (per member)',
});
export const PROJECTION = 'A projection built on the last sourced rate and the figures entered here. It informs a chairman at the table; it is not a valuation and it never enters a claim.';

const n = (v) => { const x = Number(String(v == null ? '' : v).trim()); return String(v == null ? '' : v).trim() !== '' && Number.isFinite(x) ? x : null; };
const s = (v) => (v == null ? '' : String(v)).trim();
const round2 = (x) => Math.round(x * 100) / 100;
export const money = (x) => (x < 0 ? '-' : '') + '$' + Math.abs(x).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

// input: { unit (the bargaining unit), members, annualHours, lines: [{ kind, value, basis }],
//          deductions: [{ name, annualPerMember, provision }] }
// base: lastSourcedRate(unit) or null. floor: claimValue.js RATE_CURRENCY_FLOOR - a base effective before it has been
// superseded by at least one wage round, and the claim calculator refuses it; so does this one.
export function costProposal(input, base, floor) {
  const i = input || {};
  const errors = [];
  const members = n(i.members), hours = n(i.annualHours);
  if (!s(i.unit)) errors.push('Choose the bargaining unit the proposal is for - one at a time.');
  if (members === null || members < 1 || !Number.isInteger(members)) errors.push('How many members does the proposal cover? A whole number, 1 or more.');
  if (hours === null || hours <= 0 || hours > 8784) errors.push('Annual paid hours per member is required (no default is assumed).');
  const lines = Array.isArray(i.lines) ? i.lines.filter(l => l && (s(l.value) || s(l.basis))) : [];
  const deds = Array.isArray(i.deductions) ? i.deductions.filter(d => d && (s(d.name) || s(d.annualPerMember) || s(d.provision))) : [];
  if (!lines.length) errors.push('Enter at least one proposed change.');
  const out = [];
  lines.forEach((l, k) => {
    const v = n(l.value);
    const tag = 'Change ' + (k + 1) + ': ';
    if (!LINE_KINDS[l.kind]) { errors.push(tag + 'choose what kind of change it is.'); return; }
    if (v === null) { errors.push(tag + 'enter its figure.'); return; }
    if (!s(l.basis)) { errors.push(tag + 'say where it comes from - the proposal\'s article or paragraph.'); return; }
    let perMember, work;
    if (l.kind === 'gwi_percent' || l.kind === 'hourly_increase') {
      if (!base || !Number.isFinite(base.baseHourly)) { errors.push(tag + 'this unit has no sourced base rate on file, so a rate change cannot be priced. Source the wage schedule first; a placeholder never prices a proposal.'); return; }
      if (floor && String(base.effectiveDate || '') < floor) { errors.push(tag + 'the last sourced base rate for this unit is effective ' + base.effectiveDate + ', before ' + floor + ' - it has been superseded, and pricing a raise on it would understate the cost. Source the current wage schedule first.'); return; }
      if (hours === null) return;
      const perHour = l.kind === 'gwi_percent' ? base.baseHourly * v / 100 : v / 100;
      perMember = round2(perHour * hours);
      work = (l.kind === 'gwi_percent' ? v + '% of ' + money(base.baseHourly) + '/hr' : v + ' cents/hr') + ' = ' + money(round2(perHour)) + '/hr x ' + hours + ' hrs = ' + money(perMember);
    } else {
      perMember = round2(v);
      work = money(perMember) + ' per member';
    }
    out.push({ kind: l.kind, label: LINE_KINDS[l.kind], basis: s(l.basis), perMember, work });
  });
  const dout = [];
  deds.forEach((d, k) => {
    const v = n(d.annualPerMember);
    const tag = 'Deduction ' + (k + 1) + ': ';
    if (!s(d.name)) { errors.push(tag + 'name the cost the member bears.'); return; }
    if (v === null || v < 0) { errors.push(tag + 'enter its annual cost per member.'); return; }
    if (!s(d.provision)) { errors.push(tag + 'give the provision it comes from - no deduction enters without its source.'); return; }
    dout.push({ name: s(d.name), perMember: round2(v), provision: s(d.provision) });
  });
  if (errors.length) return { ok: false, errors };
  const gross = round2(out.reduce((a, l) => a + l.perMember, 0));
  const deducted = round2(dout.reduce((a, d) => a + d.perMember, 0));
  const net = round2(gross - deducted);
  return { ok: true, errors: [], unit: s(i.unit), members, annualHours: hours, base: base || null,
    lines: out, deductions: dout,
    perMember: { gross, deducted, net }, total: { gross: round2(gross * members), deducted: round2(deducted * members), net: round2(net * members) },
    projection: PROJECTION };
}
